import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  useTheme,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid
} from '@mui/material';
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); swapped out
// the MUI icon-font set used here before (Rahim, 2026-08-25: "use better icon use from our
// new library").
// Eye dropped along with the commented-out "Click to view details" footer below.
import { ShoppingCart, Box1, Gallery, ArrowDown2, ArrowUp2, CloseCircle } from 'iconsax-react';

// Same fixed-pixel width cap TemplateMessageDisplay uses — a percentage width (the old "45%
// of parent") fights the outer pill bubble's own shrink-to-fit sizing and leaves a big empty
// gap around the card (Rahim, 2026-08-25: "the left space is too much"/"alignment off").
// Wider than the template's 300px since a product row (thumbnail + name + price + availability
// chip) needs more room to breathe (Rahim, 2026-08-25: "the box is small... make it a little
// big").
const catalogCardWidthSx = {
  maxWidth: {
    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.7), calc(100vw - 72px))",
    sm: "min(380px, calc(var(--chatbox-width, 100vw) - 96px))",
  },
  width: { xs: "100%", sm: "auto" },
  boxSizing: "border-box",
};

// status/time used to be drawn here as an absolute-positioned footer — a second, independent
// copy of the same tick-icon logic every other bubble already has, and the reason Delivered's
// ticks used to render at a different size/weight than Read's (Rahim, 2026-08-25: fixed at
// the shared ChatCard level instead — this card no longer needs its own). Dropped along with
// the status/time props now that this renders inside ChatCard's shared bubble scaffold.
//
// Visual pass (2026-08-25): brought this in line with the template cards — fixed-light
// "document" colors (not theme-following), a real pixel width cap instead of a percentage,
// and the WhatsApp-green branding pulled back to the same muted teal accent (#1F7A5C) the
// templates use, kept only as a semantic tint on the availability chip (Rahim: "dont
// emphasis on using green colour keep it simple yet elegant with proper padding").
// Meta returns this as "in stock"/"out of stock" (space-separated), but some payloads
// use "in_stock" — normalize both before comparing so the chip color doesn't invert.
const isAvailabilityInStock = (availability) =>
  String(availability || 'in_stock').toLowerCase().replace(/_/g, ' ').trim() === 'in stock';

const CatalogMessageCard = ({ data }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const renderProduct = (product, index, isLast) => {
    const inStock = isAvailabilityInStock(product.availability);
    return (
      <Box key={index}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
          }}
        >
          {/* Product image */}
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: '8px',
              backgroundColor: '#F0F2F5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {(() => {
              const hasValidImage = product.imageUrl &&
                product.imageUrl.trim() !== '' &&
                product.imageUrl !== 'null' &&
                product.imageUrl !== 'undefined' &&
                (product.imageUrl.startsWith('http') || product.imageUrl.startsWith('data:'));
              return hasValidImage ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Gallery size="20" color="#65676B" variant="Linear" />
              );
            })()}
          </Box>

          {/* Name / price / availability */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Product name is a level below the catalog title, and regular weight — 500
                still read too bold at this dark a color (Rahim, 2026-08-25: "the text is
                very bold... for small medium large", same fix applied here). */}
            <Typography
              sx={{
                fontWeight: 400,
                color: '#050505',
                fontSize: '0.8125rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.name || 'Unnamed Product'}
            </Typography>

            {product.description && (
              <Typography
                sx={{
                  color: '#65676B',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.75rem',
                  mt: '1px',
                }}
              >
                {product.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '4px' }}>
              {product.price && product.price !== '0' && (
                // Accent teal + medium weight, not the same near-black as the product name
                // right above it — the two were reading as one mixed-together line (Rahim,
                // 2026-08-25: "both use dark black so it get mixed use different font weight").
                <Typography sx={{ color: '#1F7A5C', fontWeight: 600, fontSize: '0.75rem' }}>
                  {product.price} {product.currency || 'USD'}
                </Typography>
              )}

              <Chip
                label={product.availability || 'in_stock'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 500,
                  backgroundColor: inStock ? '#EAF3EF' : '#FDF3E7',
                  color: inStock ? '#1F7A5C' : '#9A6700',
                  '& .MuiChip-label': { px: '6px' },
                }}
              />
            </Box>
          </Box>
        </Box>
        {!isLast && <Divider sx={{ borderColor: '#E6E7EB' }} />}
      </Box>
    );
  };

  const { catalogType, productData } = data;
  const { products = [], bodyText, catalogName } = productData || {};

  // Detailed Product View Component for Modal
  const DetailedProductView = ({ product, index }) => (
    <Card
      sx={{
        mb: 2,
        backgroundColor: isDark ? '#2a2a2a' : '#fff',
        border: isDark ? '1px solid #444' : '1px solid #e0e0e0',
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      <Grid container spacing={0}>
        {/* Product Image */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              width: '100%',
              height: 200,
              backgroundColor: isDark ? '#333' : '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {(() => {
              const hasValidImage = product.imageUrl &&
                product.imageUrl.trim() !== '' &&
                product.imageUrl !== 'null' &&
                product.imageUrl !== 'undefined' &&
                (product.imageUrl.startsWith('http') || product.imageUrl.startsWith('data:'));
              return hasValidImage ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null;
            })()}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Gallery size="60" color={isDark ? '#666' : '#999'} variant="Linear" />
                <Typography variant="h6" sx={{
                  color: isDark ? '#666' : '#999',
                  fontSize: '14px',
                  mt: 1
                }}>
                  No Image Available
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={8}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? '#fff' : '#222',
                mb: 1
              }}
            >
              {product.name || 'Unnamed Product'}
            </Typography>

            {product.description && (
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#bbb' : '#666',
                  mb: 2,
                  lineHeight: 1.6
                }}
              >
                {product.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {product.price && product.price !== '0' && (
                <Chip
                  label={`${product.price} ${product.currency || 'USD'}`}
                  sx={{
                    backgroundColor: '#EAF3EF',
                    color: '#1F7A5C',
                    fontWeight: 600
                  }}
                />
              )}

              <Chip
                label={product.availability || 'in_stock'}
                sx={{
                  backgroundColor: isAvailabilityInStock(product.availability) ? '#EAF3EF' : '#FDF3E7',
                  color: isAvailabilityInStock(product.availability) ? '#1F7A5C' : '#9A6700',
                  fontWeight: 500
                }}
              />

              <Chip
                label={product.condition || 'new'}
                variant="outlined"
                sx={{
                  borderColor: isDark ? '#666' : '#ccc',
                  color: isDark ? '#bbb' : '#666'
                }}
              />
            </Box>

            {product.url && (
              <Button
                variant="contained"
                size="small"
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: '#1F7A5C',
                  '&:hover': {
                    backgroundColor: '#26765a'
                  }
                }}
              >
                View Product
              </Button>
            )}
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <>
      <Card
        // Click-to-open-detail-modal disabled per Rahim, 2026-08-25: "comment out the
        // property of click to view details" — everything the modal showed (name, body
        // text, every product) is already visible inline in the card itself, so the card
        // is a static display now, not a click target. Left commented (not deleted) since
        // `modalOpen`/`setModalOpen` and the <Dialog> below still exist if this needs to
        // come back.
        // onClick={() => setModalOpen(true)}
        sx={{
          ...catalogCardWidthSx,
          borderRadius: '14px',
          overflow: 'hidden',
          // Fixed light "document" colors, not theme-following — same convention as the
          // template cards (Rahim, 2026-08-25: "as we have been doing for templates").
          border: '1px solid #DADCDE',
          backgroundColor: '#FFFFFF',
          padding: 0,
          boxShadow: 'none',
          // cursor: 'pointer',
          // transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          // '&:hover': {
          //   borderColor: '#B7BBC1',
          //   boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          // },
        }}
      >
        {/* MUI bakes a `&:last-child { padding-bottom: 24px }` rule into CardContent that
            survives `padding: 0` — see TemplateMessageDisplay for the same fix. */}
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          {/* Header: icon + name + type chip. Matches the exact heading treatment
              TemplateMessageDisplay uses for its own HEADER TEXT (fontWeight 600 /
              0.9375rem) — same "document title" font everywhere, not a heavier one-off
              (Rahim, 2026-08-25: "the heading font being used in the order confirmed
              template use the same font"). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 14px 6px 14px' }}>
            <ShoppingCart size="20" color="#65676B" variant="Linear" style={{ flexShrink: 0 }} />
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: '#050505',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {catalogName || 'Catalog'}
            </Typography>
            <Chip
              label={catalogType === 'single' ? 'Single Product' : 'Multi Product'}
              size="small"
              sx={{
                flexShrink: 0,
                backgroundColor: '#F0F2F5',
                color: '#65676B',
                fontWeight: 500,
                fontSize: '0.6875rem',
                height: '22px',
                '& .MuiChip-label': { px: '8px' },
              }}
            />
          </Box>

          {/* Body Text */}
          {bodyText && (
            <Typography
              sx={{ padding: '0 14px 10px 14px', fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.45, color: '#65676B' }}
            >
              {bodyText}
            </Typography>
          )}

          <Divider sx={{ borderColor: '#E6E7EB' }} />

          {/* Products section label — a small muted eyebrow, not a second heading: uppercase
              + letter-spacing + secondary color keep it visually subordinate to the catalog
              name above. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px 4px 14px' }}>
            <Box1 size="14" color="#8A8D91" variant="Linear" />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.6875rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#8A8D91',
              }}
            >
              Products ({products.length})
            </Typography>
          </Box>

          {products.length > 0 ? (
            <Box>
              {catalogType === 'single' ? (
                renderProduct(products[0], 0, true)
              ) : (
                <>
                  {/* Last of the visible pair never draws its own trailing divider — when
                      there are more than 2 products the explicit divider right before "Show
                      More" already provides that line, and drawing both stacked a doubled
                      border above the button (Rahim, 2026-08-25: "the top border line is
                      doubled"). */}
                  {products.slice(0, 2).map((product, index) =>
                    renderProduct(product, index, index === 1)
                  )}

                  {products.length > 2 && (
                    <>
                      <Collapse in={expanded}>
                        <Box>
                          {products.slice(2).map((product, index) =>
                            renderProduct(product, index + 2, index === products.length - 3)
                          )}
                        </Box>
                      </Collapse>

                      <Divider sx={{ borderColor: '#E6E7EB' }} />
                      {/* `all: 'unset'` + a later `color` in the same sx object silently drops
                          the color declaration when emotion serializes it — confirmed live via
                          devtools (the compiled rule's `color` stayed `unset`, so the native
                          <button> just inherited the dark-theme text color, invisible against
                          the card no matter what hex was picked). Reset only the specific
                          native-button defaults instead of `all` (Rahim, 2026-08-25: "still
                          the button is not visible for dark mood"). */}
                      <Typography
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded(!expanded);
                        }}
                        sx={{
                          border: 'none',
                          outline: 'none',
                          background: 'none',
                          margin: 0,
                          font: 'inherit',
                          textAlign: 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '10px 14px',
                          color: '#1F7A5C',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#1F7A5C14' },
                        }}
                      >
                        {expanded ? 'Show Less' : `Show ${products.length - 2} More`}
                        <Box sx={{ display: 'flex', ml: 0.5 }}>
                          {expanded ? (
                            <ArrowUp2 size="16" color="currentColor" variant="Linear" />
                          ) : (
                            <ArrowDown2 size="16" color="currentColor" variant="Linear" />
                          )}
                        </Box>
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 400, color: '#65676B', fontStyle: 'italic', padding: '8px 14px 14px 14px' }}>
              No products available
            </Typography>
          )}

          {/* Footer — "Click to view details" removed along with the click-to-open-modal
              behavior above; nothing left to point at (Rahim, 2026-08-25: "comment out
              the property of click to view details"). */}
          {/* <Divider sx={{ borderColor: '#E6E7EB' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 14px' }}>
            <Eye size="14" color="#8A8D91" variant="Linear" />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#8A8D91' }}>
              Click to view details
            </Typography>
          </Box> */}
        </CardContent>
      </Card>

      {/* Detailed Catalog Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: isDark ? '#1a1a1a' : '#fff',
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderBottom: isDark ? '1px solid #444' : '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart size="22" color="#1F7A5C" variant="Linear" />
            <Typography variant="h6" component="span" fontWeight={600} sx={{ color: isDark ? '#fff' : '#222' }}>
              {catalogName || 'Catalog'} - Detailed View
            </Typography>
            <Chip
              label={catalogType === 'single' ? 'Single Product' : 'Multi Product'}
              size="small"
              sx={{
                backgroundColor: '#1F7A5C',
                color: 'white',
                fontWeight: 500
              }}
            />
          </Box>
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{ color: isDark ? '#bbb' : '#666' }}
          >
            <CloseCircle size="22" color="currentColor" variant="Linear" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Body Text */}
          {bodyText && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? '#fff' : '#222',
                  fontWeight: 500,
                  fontStyle: 'italic'
                }}
              >
                "{bodyText}"
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Products Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box1 size="20" color={isDark ? '#bbb' : '#666'} variant="Linear" />
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: isDark ? '#fff' : '#222' }}>
                Products ({products.length})
              </Typography>
            </Box>

            {products.length > 0 ? (
              <Box>
                {products.map((product, index) => (
                  <DetailedProductView key={index} product={product} index={index} />
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box1 size="60" color={isDark ? '#666' : '#999'} variant="Linear" style={{ marginBottom: 16 }} />
                <Typography variant="h6" color="text.secondary">
                  No products available
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CatalogMessageCard;
